// Página OrderDetail - Detalle completo de un pedido
// Muestra toda la información del pedido y permite cambiar el estado

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getOrder, updateOrderStatus } from '../../services/orders.service'
import { getWarehouse } from '../../services/warehouses.service'
import { formatCurrency, formatDate } from '../../utils/helpers'
import { getImageUrl } from '../../services/cloudinary.service'
import { useSaleByOrderId } from '../../hooks/useSales'
import { generateOrderPDF } from '../../utils/pdfGenerator'
import { notifyOrderDelivered } from '../../services/notifications.service'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import './OrderDetail.css'

/**
 * Página OrderDetail
 * Muestra el detalle completo de un pedido
 */
function OrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [notification, setNotification] = useState(null)
  const [pickupWarehouse, setPickupWarehouse] = useState(null)
  
  // Verificar si ya existe una venta para este pedido
  const { sale: existingSale, loading: loadingSale } = useSaleByOrderId(order?.id)

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const orderData = await getOrder(id)
      
      if (!orderData) {
        setError('Pedido no encontrado')
        return
      }
      
      setOrder(orderData)
    } catch (err) {
      console.error('Error cargando pedido:', err)
      setError('Error al cargar el pedido')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const method = order?.fulfillment?.method === 'pickup' ? 'pickup' : 'delivery'
      const pickupWarehouseId = typeof order?.fulfillment?.pickupWarehouseId === 'string' ? order.fulfillment.pickupWarehouseId : ''
      if (method !== 'pickup' || !pickupWarehouseId) {
        setPickupWarehouse(null)
        return
      }
      try {
        const wh = await getWarehouse(pickupWarehouseId)
        if (!cancelled) setPickupWarehouse(wh || null)
      } catch (_e) {
        if (!cancelled) setPickupWarehouse(null)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [order])

  const handleStatusChange = (newStatusValue) => {
    setNewStatus(newStatusValue)
    setShowStatusDialog(true)
  }

  const confirmStatusChange = async () => {
    if (!order || !newStatus) return

    try {
      await updateOrderStatus(order.id, newStatus)
      
      setOrder(prev => ({ ...prev, status: newStatus }))
      if (newStatus === 'delivered') {
        notifyOrderDelivered(order.id).catch(() => null)
      }
      
      setNotification({
        type: 'success',
        message: `Estado del pedido actualizado a "${getStatusLabel(newStatus)}"`
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error actualizando estado:', err)
      setNotification({
        type: 'error',
        message: 'Error al actualizar el estado del pedido'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowStatusDialog(false)
      setNewStatus('')
    }
  }

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'En Proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="loading-state">
          <p>Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="order-detail-page">
        <div className="error-state">
          <p>{error || 'Pedido no encontrado'}</p>
          <Button onClick={() => navigate('/admin/pedidos')}>
            Volver a Pedidos
          </Button>
        </div>
      </div>
    )
  }

  const orderDate = order.createdAt?.toDate 
    ? formatDate(order.createdAt.toDate())
    : formatDate(new Date())

  return (
    <div className="order-detail-page">
      {/* Toast de notificación */}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="detail-header">
        <div>
          <Link to="/admin/pedidos" className="back-link">
            ← Volver a Pedidos
          </Link>
          <h1>Pedido {order.orderNumber || order.id}</h1>
          <p className="detail-subtitle">Creado el {orderDate}</p>
        </div>
        <div className="detail-status-section">
          <span className={getStatusClass(order.status)}>
            {getStatusLabel(order.status)}
          </span>
          {order.status !== 'delivered' && order.status !== 'cancelled' && (
            <select
              className="status-select-large"
              value={order.status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmado</option>
              <option value="processing">En Proceso</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          )}
          
          {/* Botón para ver/editar venta (siempre visible ya que se crea automáticamente) */}
          {!loadingSale && (
            <div className="register-sale-section">
              {existingSale ? (
                <>
                  <Link to={`/admin/ventas/${existingSale.id}`}>
                    <Button variant="secondary">
                      👁️ Ver Venta
                    </Button>
                  </Link>
                  <Link to={`/admin/ventas/nuevo/${order.id}`}>
                    <Button variant="primary">
                      ✏️ Editar Venta
                    </Button>
                  </Link>
                </>
              ) : (
                <Link to={`/admin/ventas/nuevo/${order.id}`}>
                  <Button variant="primary">
                    💵 Gestionar Venta
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="detail-content">
        {/* Información del cliente */}
        <div className="detail-section">
          <h2 className="section-title">Información del Cliente</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nombre:</label>
              <span>{order.customer?.name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{order.customer?.email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Teléfono:</label>
              <span>{order.customer?.phone || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Entrega:</label>
              <span>{order.fulfillment?.method === 'pickup' ? 'Retiro por sucursal' : 'Envío a domicilio'}</span>
            </div>
            <div className="info-item full-width">
              <label>{order.fulfillment?.method === 'pickup' ? 'Sucursal:' : 'Dirección:'}</label>
              <span>
                {order.fulfillment?.method === 'pickup'
                  ? (pickupWarehouse
                    ? `${pickupWarehouse.name || 'Sucursal'}${pickupWarehouse.address ? ` — ${pickupWarehouse.address}` : ''}`
                    : 'Sucursal seleccionada')
                  : (
                    <>
                      {order.customer?.address?.street || ''}
                      {order.customer?.address?.city && `, ${order.customer.address.city}`}
                      {order.customer?.address?.postalCode && ` (${order.customer.address.postalCode})`}
                      {order.customer?.address?.province && `, ${order.customer.address.province}`}
                    </>
                  )}
              </span>
            </div>
          </div>
        </div>

        {/* Productos del pedido */}
        <div className="detail-section">
          <h2 className="section-title">Productos del Pedido</h2>
          <div className="order-items-list">
            {order.items?.map((item, index) => {
              const imageUrl = item.image
                ? getImageUrl(item.image, { width: 100, height: 100, crop: 'fill' })
                : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'

              return (
                <div key={index} className="order-item-detail">
                  <img 
                    src={imageUrl} 
                    alt={item.productName}
                    className="item-image"
                  />
                  <div className="item-info">
                    <h3 className="item-name">{item.productName}</h3>
                    {item.sku && (
                      <p className="item-sku">SKU: {item.sku}</p>
                    )}
                    {item.variant && (
                      <p className="item-variant">
                        {item.variant.type}: {item.variant.name}
                      </p>
                    )}
                    <p className="item-quantity">Cantidad: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    <p className="item-unit-price">
                      {formatCurrency(item.price)} c/u
                    </p>
                    <p className="item-total-price">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Resumen financiero */}
        <div className="detail-section">
          <h2 className="section-title">Resumen Financiero</h2>
          <div className="financial-summary">
            <div className="summary-row">
              <span className="summary-label">Subtotal:</span>
              <span className="summary-value">{formatCurrency(order.subtotal || 0)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Envío:</span>
              <span className="summary-value">
                {order.shipping === 0 ? (
                  <span className="free-shipping">Gratis</span>
                ) : (
                  formatCurrency(order.shipping || 0)
                )}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Medio de pago:</span>
              <span className="summary-value">{order.payment?.label || 'Pendiente'}</span>
            </div>
            {order.paymentAdjustment?.amount ? (
              <div className="summary-row">
                <span className="summary-label">{order.paymentAdjustment.amount < 0 ? 'Descuento:' : 'Recargo:'}</span>
                <span className="summary-value">
                  {order.paymentAdjustment.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(order.paymentAdjustment.amount))}
                </span>
              </div>
            ) : null}
            {order.distance && (
              <div className="summary-row">
                <span className="summary-label">Distancia:</span>
                <span className="summary-value">{order.distance} km</span>
              </div>
            )}
            <div className="summary-divider"></div>
            <div className="summary-row summary-total">
              <span className="summary-label">Total:</span>
              <span className="summary-value">{formatCurrency(order.total || 0)}</span>
            </div>
          </div>
        </div>

        {/* Notas adicionales */}
        {order.notes && (
          <div className="detail-section">
            <h2 className="section-title">Notas Adicionales</h2>
            <p className="order-notes">{order.notes}</p>
          </div>
        )}

        {/* Información adicional */}
        <div className="detail-section">
          <h2 className="section-title">Información Adicional</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>ID del Pedido:</label>
              <span className="monospace">{order.id}</span>
            </div>
            <div className="info-item">
              <label>Número de Pedido:</label>
              <span className="monospace">{order.orderNumber || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Fecha de Creación:</label>
              <span>{orderDate}</span>
            </div>
            {order.updatedAt && (
              <div className="info-item">
                <label>Última Actualización:</label>
                <span>
                  {order.updatedAt?.toDate 
                    ? formatDate(order.updatedAt.toDate())
                    : formatDate(new Date())}
                </span>
              </div>
            )}
          </div>
          
          {/* Botón para generar PDF */}
          <div className="pdf-actions">
            <Button
              variant="outline"
              onClick={async () => await generateOrderPDF(order)}
            >
              📄 Generar PDF del Pedido
            </Button>
          </div>
        </div>
      </div>

      {/* Diálogo de confirmación de cambio de estado */}
      <ConfirmDialog
        isOpen={showStatusDialog}
        title="Cambiar Estado del Pedido"
        message={`¿Estás seguro de que deseas cambiar el estado del pedido a "${getStatusLabel(newStatus)}"?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={confirmStatusChange}
        onClose={() => {
          setShowStatusDialog(false)
          setNewStatus('')
        }}
      />
    </div>
  )
}

export default OrderDetail

